import React, {useState} from "react";
import {AlertCircle, BookOpen, GraduationCap, Loader2, Search} from "lucide-react";
import api from "../api/axios";

const WordSearch = () => {
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getLevelBadgeStyle = (level?: number) => {
        switch (level) {
            case 1:
                return 'bg-red-500 text-white border-red-500';      // N1
            case 2:
                return 'bg-orange-500 text-white border-orange-500'; // N2
            case 3:
                return 'bg-yellow-500 text-white border-yellow-500'; // N3
            case 4:
                return 'bg-green-500 text-white border-green-500';   // N4
            default:
                return 'bg-blue-500 text-white border-blue-500';    // N5 또는 기타
        }
    };

    // const getFontSizeClass = (text: string) => {
    //     const length = text.length;
    //     if (length === 1) return "text-5xl";
    //     if (length === 2) return "text-4xl";
    //     if (length === 3) return "text-3xl";
    //     return "text-2xl";
    // };

    // 1. 백엔드 응답 데이터와 일치하는 인터페이스 정의
    interface SearchResult {
        id?: number;
        kanji: string;
        hiragana: string
        meaning: string;
        level?: number;
        example?: string;  // 아직 DB엔 없지만 추후 확장성을 위해 남겨둠
    }

    // 2. 결과는 여러 개가 올 수 있으므로 배열([])로 관리
    const [results, setResults] = useState<SearchResult[]>([]);
    const [hasSearched, setHasSearched] = useState(false); // 검색 시도 여부

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault(); // 폼 제출 시 새로고침 방지

        if (!query.trim()) return;

        setLoading(true);
        setError(null);
        setResults([]); // 기존 결과 초기화

        try {
            const response = await api.get(`/api/words/search?q=${encodeURIComponent(query)}`);

            const data: SearchResult[] = response.data;
            data.sort((a, b) => {
                const levelA = a.level || 0;
                const levelB = b.level || 0;
                return levelB - levelA;
            });
            setResults(data);
        } catch (err) {
            console.error(err);
            setError("단어를 불러오는 중 문제가 발생했습니다.");
        } finally {
            setLoading(false);
            setHasSearched(true);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 pb-10">
            {/* 헤더 섹션 */}
            <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center justify-center gap-2">
                    <Search className="w-6 h-6 text-purple-600 dark:text-purple-400"/>
                    일본어 단어 검색
                </h2>
                <p className="text-gray-500 dark:text-gray-400">한자나 뜻을 입력하면 관련된 단어를 모두 찾아드립니다.</p>
            </div>

            {/* 검색 폼 */}
            <form onSubmit={handleSearch} className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="예: 愛, 사랑, かわいい"
                    className="w-full pl-6 pr-14 py-4 rounded-full border-2 border-purple-100 dark:border-purple-900 focus:border-purple-500 dark:focus:border-purple-700 focus:outline-none shadow-sm text-lg transition-colors placeholder:text-gray-300 dark:placeholder:text-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100"
                />
                <button
                    type="submit"
                    disabled={loading}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-purple-600 dark:bg-purple-700 text-white rounded-full hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors disabled:bg-purple-300 dark:disabled:bg-purple-900"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Search className="w-5 h-5"/>}
                </button>
            </form>

            {/* 에러 메시지 */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-2 justify-center">
                    <AlertCircle className="w-5 h-5"/>
                    {error}
                </div>
            )}

            {/* 결과 리스트 영역 */}
            <div className="space-y-4">
                {results.length > 0 ? (
                    results.map((word, index) => (
                        <div key={index}
                             className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-md border border-purple-50 dark:border-purple-900/30 hover:border-purple-200 dark:hover:border-purple-700 transition-all animate-fade-in-up">
                            <div className="flex flex-col md:flex-row gap-6 items-start">
                                {/* 왼쪽: 한자 정보 */}
                                <div className="flex flex-col items-center min-w-[100px] w-full md:w-auto">
                                    <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-xl w-full text-center mb-2">
                                        <span className="text-sm font-medium text-purple-600 dark:text-purple-400">{word.hiragana}</span>
                                        <span
                                            className="text-4xl font-extrabold text-purple-800 dark:text-purple-300 block mb-1">{word.kanji}</span>
                                    </div>

                                    {/* ✨ 3. 레벨 뱃지 (색상 적용) */}
                                    {word.level && (
                                        <span
                                            className={`px-3 py-1 text-xs font-bold rounded-full border shadow-sm flex items-center gap-1 ${getLevelBadgeStyle(word.level)}`}>
                                            <GraduationCap className="w-3 h-3"/>
                                            N{word.level}
                                        </span>
                                    )}
                                </div>

                                {/* 오른쪽: 뜻 및 예문 */}
                                <div className="flex-1 space-y-3 w-full">
                                    <div>
                                        <h3 className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                                            <BookOpen className="w-3 h-3"/>
                                            Meaning
                                        </h3>
                                        <p className="text-xl font-bold text-gray-800 dark:text-gray-100">{word.meaning}</p>
                                    </div>

                                    {/* 예문 섹션 (데이터가 없으면 안내 문구 표시) */}
                                    <div>
                                        <h3 className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mb-1">Example</h3>
                                        <div
                                            className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg border-l-4 border-purple-200 dark:border-purple-900 text-sm leading-relaxed">
                                            {word.example ? (
                                                word.example
                                            ) : (
                                                <span className="text-gray-400 dark:text-gray-600 italic">
                                                    예문 데이터가 준비되지 않았습니다. <br/>
                                                    (추후 AI 기능 업데이트 예정)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    // 검색은 했으나 결과가 0개일 때
                    hasSearched && !loading && !error && (
                        <div className="text-center py-10 text-gray-400 dark:text-gray-500">
                            <p>검색 결과가 없습니다.</p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default WordSearch;